import React from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { Box, Grid, Typography, List, ListItem, ListItemIcon } from "@mui/material";
import Title from "bundles/Main/components/Title";
import FadeInUp from "bundles/common/utils/FadeInUp";
import ContactForm from "bundles/Main/components/ContactForm";
import Founders from "images/4-founders.png";
import Greg from "images/greg-picture1.png";
import Alex from "images/alex-picture.png";
import Kakoullis from "images/kakoullis-picture.png";
import Leonidas from "images/leonidas-picture.png";

const teamMembers = [
  {
    image: Greg,
    name: "Dr. Greg Rozenberg",
    role: "Cofounder & CEO",
    bulletPoints: ["Medical Doctor (MD)", "Born and raised in Brussels, Belgium", "Graduated from the European University of Cyprus, where he studied medicine", "Co-founder of HealthMission Africa and Health Community Outreach Program",
      "CEO of HealthMission Africa", "Oversees all organizational departments and ensure they work cohesively towards our shared goals"]
  },
  {
    image: Alex,
    name: "Dr. Alex Ruben",
    role: "Cofounder & COO",
    bulletPoints: ["Medical officer", "Born and raised in Arusha, Tanzania", "Graduated from KAM COLLEGE OF HEALTH SCIENCES in Dar Es Salaam, where he studied clinical medicine",
      "Doctor in charge of the Perfect Dispensary in Arusha, Tanzania", "Co-founder of HealthMission Africa and Health Community Outreach Program", "Coordinator and supervisor of interns and volunteers in Tanzania", "Consults and treats patients during community outreach projects"]
  },
  {
    image: Kakoullis,
    name: "Prof. Dr. Stylianos Kakoullis",
    role: "Academic Director",
    bulletPoints: [
      "Associate Professor of Internal, Pulmonary & Intensive Care Medicine at the European University Cyprus.",
      "Senior Consultant Pulmonologist and Critical Care Physician",
      "Academic Director of HealthMission Africa",
      "Oversees the clinical rotations and elective placements in Tanzania with particular interest in the Emergency & ICU Clinical Program.",
      "Completed fellowship training in Critical Care Medicine at the University of Pittsburgh Medical Center and Pulmonary Medicine at Columbia University, New York",
      "Former Director of the Intensive Care Unit at Apollonion Private Hospital, Cyprus",
      "Leads the academic development of HealthMission Africa, ensuring all clinical programs combine evidence-based teaching with immersive hands-on hospital experience"
    ]
  },
  {
    image: Leonidas,
    name: "Dr. Leonidas Bousdoukos",
    role: "Cofounder & Volunteer and Outreach Coordinator",
    bulletPoints: ["Medical Doctor (MD)", "Born and raised in Hamburg, Germany", "Currently works as a cardiac surgery resident at the university medical-center in Hamburg, Germany",
      "Co-founder of HealthMission Africa and Health Community Outreach Program", "Planning process for selecting doctors who participate in our community outreach projects", "Supervises HCOP’s medical supply list and diagnostic devices", "Oversees the medical preparation of interns and volunteers before their internships."]
  }
];

const MemberInfo = ({ image, name, role, bulletPoints, reverse }) => (
  <Grid
    container
    spacing={2}
    direction={{ xs: "column", md: reverse ? "row-reverse" : "row" }}
    alignItems="center"
    justifyContent="center"
    sx={{
      maxWidth: "1200px",
      width: "100%",
      mx: "auto",
      mb: 4,
      px: { xs: 2, md: 4 }
    }}
  >
    <Grid
      item
      xs={12}
      md={4}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%"
      }}
    >
      <FadeInUp style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <Box
          component="img"
          src={image}
          alt={name}
          sx={{
            display: "block",
            width: "100%",
            maxWidth: { xs: "280px", sm: "320px", md: "100%" },
            height: "auto",
            mx: "auto"
          }}
        />
      </FadeInUp>
    </Grid>
    <Grid
      item
      xs={12}
      md={8}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        px: { xs: 1, md: 4 },
        overflow: "auto",
        maxHeight: "100%",
        maxWidth: "100%",
        width: "100%"
      }}
    >
      <FadeInUp>
        <Typography variant="h3" style={{ marginBottom: "16px", fontSize: "22px" }}><strong>{name}</strong></Typography>
      </FadeInUp>
      <FadeInUp>
        <Typography variant="h5" style={{fontSize: "18px" }}>{role}</Typography>
      </FadeInUp>
      <List>
        {bulletPoints.map((point, index) => (
          <FadeInUp key={ index }>
            <ListItem style={{ fontSize: "16px" }}>
              <ListItemIcon sx={{ minWidth: "32px" }}>•</ListItemIcon>
              {point}
            </ListItem>
          </FadeInUp>
        ))}
      </List>
    </Grid>
  </Grid>
);


const AboutUs = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const title = "<span style='font-weight: 400;'>Meet</span> <span style='font-weight: 800;'><em>the Team<em/></span>";

  return (
    <Box style={{ width: "100%" }}>
      <Title text={title} />
      <FadeInUp>
        <Box sx={{ maxWidth: "80%", margin: "32px auto" }}>
          <Grid container spacing={isDesktop ? 4 : 2} alignItems="center">
            <Grid item xs={12} md={6}>
              <h2>How it all started...</h2>
              {!isDesktop &&
                <FadeInUp style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                  <Box
                    component="img"
                    src={Founders}
                    alt="Founders"
                    sx={{
                      display: "block",
                      width: "100%",
                      maxWidth: "360px",
                      height: "auto",
                      mt: 3,
                      mx: "auto"
                    }}
                  />
                </FadeInUp>
              }
              <Typography sx={{ mt: 6, fontSize: "16px !important" }}>
                The foundations of HealthMission Africa and the Health Community Outreach Program were laid in 2022 during a transformative experience in Tanzania. At the time, Greg and Leo, both medical students at the European University of Cyprus, were completing a clinical internship in the emergency department of Mount Meru Regional Referral Hospital in Arusha. During this period, they witnessed firsthand the challenges many people faced in accessing medical care, particularly for those living in rural areas.<br /><br />
                Motivated by these observations, they began working with local partners in Arusha, including Alex, who operated a community dispensary and was actively involved in providing care to underserved populations. Together, they organized a community outreach initiative aimed at bringing medical services directly to villages with limited access to healthcare.<br /><br />
                This collaboration led to the organization of a two-day medical camp in nearby communities, during which more than 600 patients received consultations, treatments, and essential medications. The experience highlighted both the urgent need for accessible healthcare services and the potential for meaningful collaboration between international medical trainees and local healthcare providers.<br /><br />
                After returning to Cyprus, Greg and Leo found that many fellow students were eager to take part in similar experiences that combined clinical learning with community engagement. Recognizing this growing interest and the positive impact such initiatives could have, the project gradually evolved into a more structured program.<br /><br />
                This led to the creation of HealthMission Africa and the Health Community Outreach Program, organizations dedicated to facilitating healthcare internships, volunteering opportunities, and community outreach initiatives in Tanzania. Through these programs, students and healthcare professionals from around the world are able to gain hands-on experience in a global health setting while contributing to healthcare access in underserved communities.
              </Typography>
            </Grid>
            {isDesktop &&
              <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: "center" }}>
                <FadeInUp style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                  <Box
                    component="img"
                    src={Founders}
                    alt="Founders"
                    sx={{ display: "block", width: "100%", height: "auto", mx: "auto" }}
                  />
                </FadeInUp>
              </Grid>
            }
          </Grid>
        </Box>
      </FadeInUp>
      {teamMembers.map((member, index) => (
        <MemberInfo key={index} {...member} reverse={index % 2 !== 0} />
      ))}
      <FadeInUp>
        <Typography variant="h3" style={{ margin: isDesktop ? "16px 120px" : "16px 32px", fontSize: isDesktop ? "16px" : "14px" }}>
          *HealthMission Africa, registered on September 12, 2023, is a Cypriot company with its office located at Irakleitou, 15. 2335, Lakatamia, Cyprus.
        </Typography>
      </FadeInUp>
      <ContactForm />
    </Box>
  );
};

export default AboutUs;
